






































































import { computed, defineComponent, PropType, ref, toRefs } from '@vue/composition-api';
import { i18n } from '@tt4b/ttam-i18n';
import { bitrateFormatter } from '@tt4b/ttam-utils';
import { useMaterialVerify } from '@tt4b/ttam-hooks';
import { AdgroupFormData, FullNormalFormData, InventoryFlow } from '@tt4b/ttam-enums';
import TtamALink from 'src/components/common/a-link/a-link.vue';

const InventoryLabelMap = {
  [InventoryFlow.ALL]: i18n.t('placement_all_apps'),
  [InventoryFlow.TIKTOK]: i18n.t('placement_tiktok'),
  [InventoryFlow.TOPBUZZ]: i18n.t('placement_news_feed_app'),
  [InventoryFlow.VIGO]: i18n.t('placement_vigo'),
  [InventoryFlow.TIKTOKADNETWORK]: i18n.t('placement_tiktok_ad_network'),
  // [InventoryFlow.HELO]: i18n.t('placement_helo'),
};

const name = 'ttam-material-upload-requirement';

export default defineComponent({
  name,

  components: { TtamALink },

  props: {
    isVideo: { type: Boolean, default: true },
    isImage: { type: Boolean, default: false },
    isNative: { type: Boolean, default: false },
    isUseHideBtn: { type: Boolean, default: false },
    isRecommendVisible: { type: Boolean, default: true },
    linkText: { type: String, default: '' },
    linkHref: { type: String, default: '' },
    inventoryArr: { type: Array as PropType<InventoryFlow[]>, default: () => [InventoryFlow.TIKTOK] },
    adgroupData: { type: Object as PropType<AdgroupFormData>, default: () => ({}) },
    creativeData: { type: Object as PropType<FullNormalFormData>, default: () => ({}) },
  },

  setup(props) {
    const { isVideo, isImage, isNative, inventoryArr, adgroupData, creativeData } = toRefs(props);
    const { rules: verifyRules } = useMaterialVerify({
      adgroupFormData: adgroupData,
      creativeFormData: creativeData,
    });

    const isVisible = ref(false);

    /** 格式化素材要求 */
    function formatRequirement(str: string): { title: string; content: string }[] {
      return str.split('|').map(item => {
        /** 兼容中英文 : / ： */
        const splitTarget = item.includes(':') ? ':' : '：';
        let arr = item.split(splitTarget);

        return { title: arr[0] ?? '', content: arr[1] ?? '' };
      });
    }

    /** 素材要求 */
    const requirement = computed(() => {
      let str = '';
      if (isVideo.value) {
        str = i18n.t('upload_tb_video_specification') as string;
      }
      if (isImage.value) {
        str = i18n.t('upload_image_specification') as string;
      }

      return formatRequirement(str);
    });

    function genRatioRecommend(ratio) {
      const genDetail = ({ width, height, label }) => {
        let prefix = '';
        if (width === height) {
          prefix = i18n.t('ad_creative_aspect_square') as string;
        } else if (width > height) {
          prefix = i18n.t('ad_creative_aspect_horizontal') as string;
        } else {
          prefix = i18n.t('ad_creative_aspect_vertical') as string;
        }

        return `${prefix}(${label})`;
      };
      const content = ratio.map(templateOption => genDetail(templateOption)).join(' / ');

      return {
        label: '',
        contents: [content],
      };
    }

    function genResolutionRecommend(resolution) {
      const genDetail = ({ width, height }) => {
        let prefix = '';
        if (width === height) {
          prefix = i18n.t('ad_creative_aspect_square') as string;
        } else if (width > height) {
          prefix = i18n.t('ad_creative_aspect_horizontal') as string;
        } else {
          prefix = i18n.t('ad_creative_aspect_vertical') as string;
        }

        return `${prefix}(${width}*${height})`;
      };
      const content = resolution?.map(templateOption => genDetail(templateOption)).join(' / ');

      return {
        label: i18n.t('ad_resolution') as string,
        contents: [content],
      };
    }

    function genBitrateRecommend(bitrate) {
      const recommend = bitrate?.recommend;
      const content = recommend ? `≥ ${bitrateFormatter(recommend)}kbps` : '';

      return {
        label: i18n.t('ad_bitrate') as string,
        contents: [content],
      };
    }

    function genDurationRecommend(duration) {
      const { min, max } = duration ?? {};
      /** 小数点是平台侧留的buffer，给用户展示时去掉buffer */
      const int = (v, m = 'ceil') => Math[m](v);
      let content = '';
      if (min && max) {
        content = `${int(min)}-${int(max, 'floor')}s`;
      } else if (min) {
        content = `≥ ${int(min)}s`;
      } else if (max) {
        content = `≤ ${int(max, 'floor')}s`;
      }

      return {
        label: i18n.t('ad_duration') as string,
        contents: [content],
      };
    }

    function genSafeZoneRecommend(safeZone) {
      const { left, right, top, bottom } = safeZone ?? {};
      const gen = (zone, prefix) => {
        if (!zone) {
          return '';
        }
        const { min, max } = zone;
        if (min && max) {
          return `${prefix}: ${min}-${max}px`;
        }
        if (min || max) {
          return `${prefix}: ${min || max}px`;
        }

        return '';
      };
      const contents = [
        gen(left, i18n.t('module_ad_recommendation_safe_zone_left')),
        gen(right, i18n.t('module_ad_recommendation_safe_zone_right')),
        gen(top, i18n.t('module_ad_recommendation_safe_zone_top')),
        gen(bottom, i18n.t('module_ad_recommendation_safe_zone_bottom')),
      ];

      return {
        label: i18n.t('module_ad_recommendation_safe_zone') as string,
        contents,
        hint: i18n.t('module_ad_recommendation_safe_zone_hint') as string,
        helpLink: '/help/article?aid=6709434465828995077',
      };
    }

    /** 各个版位视频素材要求 */
    const videoRecommendations = computed(() => {
      const result: {
        supported: [];
        ratio: { label: string; contents: string[] }[];
      } = { ratio: [], supported: [] };

      inventoryArr.value?.forEach(flowKey => {
        const rule = verifyRules.value.find(ru => ru.key === flowKey);
        if (!rule || !rule.videoRule) {
          return;
        }
        const { ratio = [], resolution = [], bitrate, duration, safeZone } = rule.videoRule;

        if (ratio.length) {
          result.ratio.push(genRatioRecommend(ratio));
        }

        const recommends: {
          label: string;
          contents: string[];
          hint?: string;
          helpLink?: string;
        }[] = [];

        if (resolution.length) {
          recommends.push(genResolutionRecommend(resolution));
        }
        if (bitrate) {
          recommends.push(genBitrateRecommend(bitrate));
        }
        if (duration) {
          recommends.push(genDurationRecommend(duration));
        }
        if (safeZone) {
          recommends.push(genSafeZoneRecommend(safeZone));
        }

        if (!recommends.length) {
          return;
        }

        result.supported.push({
          flowKey,
          label: InventoryLabelMap[flowKey],
          recommends,
        } as never);
      });

      return result;
    });

    /** 各个版位图片素材要求 */
    const imageRecommendations = computed(() => {
      const supported = inventoryArr.value?.map(inventoryKey => {
        const rule = verifyRules.value.filter(ru => ru.key === inventoryKey)[0];
        if (!rule || !rule.imageRule || rule.imageRule?.unsupported) {
          return null;
        }

        const templateOptions = (rule.imageRule.resolution ?? { types: [] }).types; // 支持的图片模板

        return {
          inventoryKey,
          label: InventoryLabelMap[inventoryKey],
          content: templateOptions.map(({ width, height }) => `${width}*${height}px`).join(' / '),
        };
      });

      return { supported: supported?.filter(i => i) };
    });

    return {
      isVisible,
      requirement,
      imageRecommendations,
      videoRecommendations,
    };
  },
});
